import { useState, useMemo } from 'react';
import { useLocale } from './useLocale';
import { ReviewResponseDto } from '@bitecritic-api/src/reviews/dto/review.response.dto';
import { ReviewTranslationsDto } from '@bitecritic-api/src/reviews/dto/review-translation.dto';

interface UseReviewTranslationProps {
  review: ReviewResponseDto | undefined;
}

export const useReviewTranslation = ({ review }: UseReviewTranslationProps) => {
  const { currentLocale } = useLocale();
  const [showOriginal, setShowOriginal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    currentLocale.language,
  );

  // Get available translation languages
  const availableLanguages = useMemo(() => {
    return review?.translations ? Object.keys(review.translations) : [];
  }, [review?.translations]);

  // Determine if the review's original language is different from user's language
  const isOriginalLanguageDifferent = useMemo(() => {
    return review?.languageCode !== currentLocale.language;
  }, [review?.languageCode, currentLocale.language]);

  // Determine if we have a translation for the current locale
  const hasTranslationForCurrentLocale = useMemo(() => {
    return review?.translations?.[currentLocale.language] !== undefined;
  }, [review?.translations, currentLocale.language]);

  // Determine if we should show the translation toggle
  // Show it when:
  // 1. The original language is different from user's language
  // 2. AND we have a translation for the user's language
  const shouldShowTranslationToggle = useMemo(() => {
    return isOriginalLanguageDifferent && hasTranslationForCurrentLocale;
  }, [isOriginalLanguageDifferent, hasTranslationForCurrentLocale]);

  // Get the appropriate review text based on language selection and toggle state
  const reviewText = useMemo(() => {
    if (!review) return '';

    // If showing original, return the original text
    if (showOriginal) {
      return review.reviewText;
    }

    // If the review is already in the user's language, return the original text
    if (review.languageCode === currentLocale.language) {
      return review.reviewText;
    }

    // If we have a translation for the user's language, return that
    if (review.translations?.[currentLocale.language]) {
      return review.translations?.[currentLocale.language]?.reviewText || '';
    }

    // If we have a translation for the selected language, return that
    if (
      selectedLanguage &&
      review.translations?.[selectedLanguage as keyof ReviewTranslationsDto] !== undefined
    ) {
      return review.translations?.[selectedLanguage as keyof ReviewTranslationsDto]?.reviewText || '';
    }

    // Fallback to original text
    return review.reviewText;
  }, [review, showOriginal, selectedLanguage, currentLocale.language]);

  // Determine if we're currently showing a translation
  const isShowingTranslation = useMemo(() => {
    if (!review) return false;

    // If explicitly showing original, we're not showing a translation
    if (showOriginal) return false;

    // If the review is in the user's language, we're not showing a translation
    if (review.languageCode === currentLocale.language) return false;

    // If we have a translation for the user's language, we're showing a translation
    if (review.translations?.[currentLocale.language]) return true;

    // If we have a translation for the selected language, we're showing a translation
    if (
      selectedLanguage &&
      review.translations?.[selectedLanguage as keyof ReviewTranslationsDto] !== undefined
    )
      return true;

    // Otherwise, we're not showing a translation
    return false;
  }, [review, showOriginal, selectedLanguage, currentLocale.language]);

  // Toggle between original and translated text
  const toggleTranslation = () => {
    setShowOriginal(!showOriginal);
  };

  // Change the selected language for translation
  const changeLanguage = (language: string) => {
    setSelectedLanguage(language);
    setShowOriginal(false);
  };

  return {
    reviewText,
    showOriginal,
    toggleTranslation,
    changeLanguage,
    selectedLanguage,
    availableLanguages,
    shouldShowTranslationToggle,
    originalLanguage: review?.languageCode,
    isOriginalLanguageDifferent,
    hasTranslationForCurrentLocale,
    isShowingTranslation,
  };
};
